// Lightbox — native <dialog> full-screen viewer for the 6 gallery slides.
// Arrow keys / on-screen buttons cycle; ESC and backdrop click close.
import { useCallback, useEffect, useRef } from "react";
import { GALLERY_CAPTIONS, GALLERY_COUNT } from "./content";

interface LightboxProps {
  readonly index: number | null; // 0-based; null = closed
  readonly onClose: () => void;
  readonly onNavigate: (index: number) => void;
}

export function Lightbox({ index, onClose, onNavigate }: LightboxProps) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dlg = ref.current;
    if (!dlg) return;
    if (index !== null && !dlg.open) dlg.showModal();
    if (index === null && dlg.open) dlg.close();
  }, [index]);

  const step = useCallback(
    (delta: number) => {
      if (index === null) return;
      onNavigate((index + delta + GALLERY_COUNT) % GALLERY_COUNT);
    },
    [index, onNavigate],
  );

  useEffect(() => {
    if (index === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") step(-1);
      if (e.key === "ArrowRight") step(1);
      // Safety net: some embedded Chromium builds fail to deliver the
      // native ESC-to-close on full-size fixed-content dialogs; closing
      // here is a no-op where the UA already handled it (dialog no longer
      // open by the time the keydown bubbles).
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, step, onClose]);

  return (
    <dialog
      ref={ref}
      className="wv-h2-lightbox"
      onClose={onClose}
      onClick={(e) => {
        if (e.target === ref.current) onClose();
      }}
    >
      {index !== null && (
        <>
          <img
            className="wv-h2-lightbox-img"
            src={`/static/images/index/slides/${index + 1}.jpg`}
            alt="随便MC"
          />
          <button className="wv-h2-lightbox-btn wv-h2-lightbox-btn--prev" onClick={() => step(-1)} aria-label="上一张">
            ‹
          </button>
          <button className="wv-h2-lightbox-btn wv-h2-lightbox-btn--next" onClick={() => step(1)} aria-label="下一张">
            ›
          </button>
          <button className="wv-h2-lightbox-close" onClick={onClose} aria-label="关闭">
            ×
          </button>
          <div className="wv-h2-lightbox-cap">{GALLERY_CAPTIONS[index]}</div>
        </>
      )}
    </dialog>
  );
}
